// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CartLineContainer, { CartLineContainerProps } from "@flip/App/Components/Cart/CartLineContainer";
import LinesHeader from "@flip/App/Components/Cart/LinesHeader";
import LegacyManufacturer from "@flip/App/Components/Product/LegacyManufacturer";
import ProductMultipleSaleQty from "@flip/App/Components/Product/MultipleSaleQty";
import ProductMinimumOrderQty from "@flip/App/Components/Product/ProductMinimumOrderQty";
import cartPageStyles from "@flip/App/SharedStyle/CartPageStyles";
import ProductBrand, { ProductBrandStyles } from "@flip/Overrides/Components/ProductBrand";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@flip/Overrides/Components/ProductPartNumbers";
import { cartLineCardExpandedStyles } from "@flip/Overrides/Widgets/Cart/CartLineCardExpanded";
import { isQuoteOnly, parseBoolean } from "@flip/XDL/Utilities";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import calculateTotal from "@insite/client-framework/Store/Pages/QuickOrder/Handlers/CalculateTotal";
import changeProductQty from "@insite/client-framework/Store/Pages/QuickOrder/Handlers/ChangeProductQty";
import removeProduct from "@insite/client-framework/Store/Pages/QuickOrder/Handlers/RemoveProduct";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { ClickableProps } from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    productInfos: state.pages.quickOrder.productInfos,
    products: state.pages.quickOrder.productInfos.map(o => getProductState(state, o.productId).value) as (
        | ProductModel
        | undefined
    )[],
    total: state.pages.quickOrder.total,
    currencySymbol: state.context.session.currency?.currencySymbol || "",
    showPricing: state.context.showPricing,
});

const mapDispatchToProps = {
    calculateTotal,
    changeProductQty,
    removeProduct,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface QuickOrderProductListStyles {
    wrapper?: InjectableCss;
    cardContainer?: CardContainerStyles;
    gridContainer?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    productInfoGridItem?: GridItemProps;
    productInfoContainer?: GridContainerProps;
    brandAndProductDescriptionGridItem?: GridItemProps;
    brand?: ProductBrandStyles;
    productDescription?: ProductDescriptionStyles;
    productNumberText?: TypographyPresentationProps;
    availability?: ProductAvailabilityStyles;
    priceAndQuantityGridItem?: GridItemProps;
    priceAndQuantityContainer?: GridContainerProps;
    priceGridItem?: GridItemProps;
    price?: ProductPriceStyles;
    productMinimumOrderQty?: TypographyProps;
    productMultipleSaleQty?: TypographyProps;
    quantityGridItem?: GridItemProps;
    quantityOrdered?: TextFieldProps;
    extendedPriceGridItem?: GridItemProps;
    extendedPriceText?: SmallHeadingAndTextStyles;
    removeProductGridItem?: GridItemProps;
    removeProductClickable?: ClickableProps;
    removeProductIcon?: IconPresentationProps;
    addToListLink?: ProductAddToListLinkStyles;
    totalText?: TypographyPresentationProps;
    // XNG
    availabilityText?: ProductAvailabilityStyles;
    availabilityLink?: ProductAvailabilityStyles;
    removeProductLink?: LinkPresentationProps;
    lineInnerContainer?: CartLineContainerProps;
    productPartNumbers?: ProductPartNumbersStyles;
    qtyLable?: TypographyProps;
}

export const quickOrderProductListStyles: QuickOrderProductListStyles = {
    wrapper: {
        css: css`
            padding: 0 20px 20px;
            margin-top: 20px;
        `,
    },
    // cardContainer: {
    //     cardDivider: {
    //         css: css`
    //             margin: 0 20px;
    //             width: 100%;
    //             border-top: 1px solid ${getColor("common.border")};
    //             &:last-child {
    //                 border-bottom: 1px solid ${getColor("common.border")};
    //             }
    //         `,
    //     },
    // },
    // productImageGridItem: {
    //     width: 2,
    //     css: css`
    //         ${({ theme }: { theme: BaseTheme }) =>
    //             breakpointMediaQueries(theme, [
    //                 css`
    //                     padding-left: 0;
    //                     padding-right: 0;
    //                     font-size: 10px;
    //                 `,
    //                 css`
    //                     padding-left: 0;
    //                     padding-right: 0;
    //                     font-size: 10px;
    //                 `,
    //                 css`
    //                     padding-left: 0;
    //                 `,
    //                 css`
    //                     padding-left: 0;
    //                 `,
    //                 css`
    //                     padding-left: 0;
    //                 `,
    //             ])}
    //     `,
    // },
    // productInfoGridItem: { width: [7, 7, 7, 8, 8] },
    // brandAndProductDescriptionGridItem: {
    //     width: [12, 12, 12, 6, 5],
    //     css: css`
    //         flex-direction: column;
    //     `,
    // },
    // availability: {
    //     container: {
    //         css: css`
    //             margin-top: 10px;
    //         `,
    //     },
    // },
    // priceAndQuantityGridItem: { width: [12, 12, 12, 6, 7] },
    // priceGridItem: {
    //     width: [12, 12, 4, 4, 4],
    //     css: css`
    //         flex-direction: column;
    //         justify-content: center;
    //     `,
    // },
    // quantityGridItem: { width: [6, 6, 5, 5, 4] },
    // quantityOrdered: {
    //     labelPosition: "left",
    //     labelProps: {
    //         css: css`
    //             width: auto;
    //             display: inline-flex;
    //         `,
    //     },
    //     cssOverrides: {
    //         formInputWrapper: css`
    //             width: 70px;
    //         `,
    //     },
    // },
    // extendedPriceGridItem: { width: [6, 6, 3, 3, 4] },
    // removeProductGridItem: {
    //     css: css`
    //         text-align: center;
    //         display: block;
    //     `,
    //     width: [3, 3, 3, 2, 2],
    // },
    // removeProductClickable: {
    //     css: css`
    //         margin-left: auto;
    //         margin-right: auto;
    //         margin-bottom: 15px;
    //     `,
    // },
    // removeProductIcon: { src: XCircle },
    productImage: {
        ...cartPageStyles.cartLine?.productImage,
    },
    // XNG-Changes: FLIP-1021
    productDescription: {
        ...cartPageStyles.cartLine?.productDescription,
    },
    // XNG-Changes: FLIP-1030
    availabilityText: {
        ...cartPageStyles.cartLine?.productAvailability,
    },
    // XNG-Changes: FLIP-1031
    productMinimumOrderQty: {
        css: css`
            margin-bottom: 4px;
        `,
    },
    productMultipleSaleQty: {
        css: css`
            margin-bottom: 4px;
        `,
    },
    price: {
        ...cartPageStyles.cartLine?.productPrice,
    },
    quantityOrdered: {
        label: "",
        cssOverrides: {
            formInputWrapper: css`
                display: flex;
                justify-content: flex-end;
            `,
            inputSelect: css`
                width: 55px;
            `,
        },
    },
    extendedPriceText: {
        ...cartPageStyles.cartLine?.extendedUnitNetPriceText,
    },
    qtyLable: {
        ...cartPageStyles.cartLine?.extendedUnitNetPriceText?.extendedStyles?.heading,
    },
    removeProductLink: {
        ...cartPageStyles.cartLine?.removeLink,
    },
    totalText: {
        css: css`
            float: right;
            font-weight: bold;
            margin: 15px 0;
        `,
    },
    // XNG-Changes: FLIP-1030
    productPartNumbers: {
        ...cartLineCardExpandedStyles.productPartNumbers,
    },
};

const styles = quickOrderProductListStyles;
const StyledSection = getStyledWrapper("section");

const QuickOrderProductList = ({
    products,
    productInfos,
    total,
    currencySymbol,
    showPricing,
    calculateTotal,
    changeProductQty,
    removeProduct,
}: Props) => {
    useEffect(() => calculateTotal(), [products]);

    const removeProductHandler = (productInfo: ProductInfo) => {
        removeProduct({ productId: productInfo.productId, unitOfMeasure: productInfo.unitOfMeasure });
    };

    if (!products || products.length === 0) {
        return null;
    }

    const productListDisplay = products.map((product, index) => {
        if (!product || index >= productInfos.length) {
            return;
        }

        const productInfo = productInfos[index];
        // XNG-Changes: FLIP-1068
        const isZeroQtyOnHand =
            productInfo?.inventory?.qtyOnHand !== undefined && productInfo?.inventory?.qtyOnHand <= 0;
        const productContext: ProductContextModel = {
            product,
            productInfo,
            onQtyOrderedChanged: qtyOrdered => {
                changeProductQty({
                    qtyOrdered,
                    productId: product.id,
                    unitOfMeasure: productInfo.unitOfMeasure,
                    product,
                });
            },
        };

        return (
            <ProductContext.Provider value={productContext} key={product.id + productInfo.unitOfMeasure}>
                {/* XNG-Changes: Changing structure */}
                <CartLineContainer>
                    <CartLineContainer.Image>
                        <ProductImage product={productContext} extendedStyles={styles.productImage} />
                    </CartLineContainer.Image>
                    {/* XNG-Changes: FLIP-507 */}
                    <CartLineContainer.Description>
                        <ProductBrand
                            brand={product.brand!}
                            isAuthorized={parseBoolean(product.properties.isAuthorizedBrand)}
                            extendedStyles={styles.brand}
                        />
                        <ProductDescription product={productContext} extendedStyles={styles.productDescription} />
                        <ProductPartNumbers
                            productNumber={product.productNumber}
                            customerProductNumber={product.customerProductNumber}
                            manufacturerItem={product.manufacturerItem}
                            sku={product.detail?.sku}
                            extendedStyles={styles.productPartNumbers}
                        />
                        {/* XNG-Changes */}
                        {product.properties?.legacyManufacturer && (
                            <LegacyManufacturer
                                legacyManufacturer={product.properties.legacyManufacturer}
                                legacyManufacturerLink={product.properties.legacyManufacturerDetailUrl}
                                isAuthorized={parseBoolean(
                                    product.properties.isLegacyManufacturerAnAuthorizedSupplier,
                                )}
                            />
                        )}
                    </CartLineContainer.Description>
                    <CartLineContainer.Availability>
                        {!product.quoteRequired && (
                            <ProductContextAvailability extendedStyles={styles.availabilityText} />
                        )}
                    </CartLineContainer.Availability>
                    {/* // XNG-Changes: FLIP-1031 */}
                    <CartLineContainer.MinimumOrderQty>
                        <ProductMinimumOrderQty product={product} extendedStyles={styles.productMinimumOrderQty} />
                    </CartLineContainer.MinimumOrderQty>
                    <CartLineContainer.MultipleSaleQty>
                        <ProductMultipleSaleQty product={product} extendedStyles={styles.productMultipleSaleQty} />
                    </CartLineContainer.MultipleSaleQty>
                    {/* XNG-Changes: FLIP-1032 */}
                    <CartLineContainer.Price>
                        <ProductPrice
                            product={productContext}
                            currencySymbol={currencySymbol}
                            showSavings={false}
                            showLabel={true}
                            showPack={false}
                            extendedStyles={styles.price}
                            {...{ labelOverride: translate("Price/Unit") }}
                        />
                    </CartLineContainer.Price>
                    <CartLineContainer.Qty>
                        <Typography {...styles.qtyLable}>{translate("QTY")}</Typography>
                        <ProductQuantityOrdered extendedStyles={styles.quantityOrdered} labelOverride="" />
                    </CartLineContainer.Qty>
                    {/* XNG-Changes: FLIP-999 */}
                    <CartLineContainer.Total>
                        {!product.quoteRequired &&
                        !productInfo.pricing?.requiresRealTimePrice &&
                        showPricing &&
                        !isQuoteOnly(
                            product.quoteRequired,
                            product.allowZeroPricing,
                            productInfo.pricing,
                            undefined,
                            isZeroQtyOnHand,
                        ) ? (
                            <SmallHeadingAndText
                                {...styles.extendedPriceText}
                                data-test-selector="quickOrder_ProductPrice"
                                heading={translate("Total")}
                                text={productInfo.pricing?.extendedUnitNetPriceDisplay || ""}
                            />
                        ) : (
                            <SmallHeadingAndText
                                {...styles.extendedPriceText}
                                data-test-selector="quickOrder_ProductPrice"
                                heading={translate("Total")}
                                text="Quote Only"
                            />
                        )}
                    </CartLineContainer.Total>
                    <CartLineContainer.AddToList>
                        <ProductAddToListLink extendedStyles={styles.addToListLink} />
                    </CartLineContainer.AddToList>
                    <CartLineContainer.Remove>
                        <Link
                            {...styles.removeProductLink}
                            onClick={() => removeProductHandler(productInfo)}
                            data-test-selector="quickOrder_RemoveProduct"
                            aria-label="Remove"
                        >
                            <VisuallyHidden>{translate("Remove item")}</VisuallyHidden>
                        </Link>
                    </CartLineContainer.Remove>
                </CartLineContainer>
            </ProductContext.Provider>
        );
    });

    return (
        <>
            <StyledSection {...styles.wrapper}>
                <LinesHeader />
                {productListDisplay}
            </StyledSection>
            {showPricing && (
                <Typography {...styles.totalText} data-test-selector="quickOrder_TotalPrice">
                    {`${translate("Total")}`} <LocalizedCurrency amount={total} />
                </Typography>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(QuickOrderProductList),
    definition: {
        group: "Quick Order",
        icon: "List",
        allowedContexts: ["QuickOrderPage"],
    },
};

export default widgetModule;
